import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, CalendarRange, Check, X } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeaveBalancePage = () => {
  const [employees, setEmployees] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    employee_name: "",
    leave_type: "annual",
    start_date: "",
    end_date: "",
    days: 1,
    reason: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [employeesRes, leaveRes] = await Promise.all([
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/leave-requests`)
      ]);
      setEmployees(employeesRes.data.filter(e => e.status === "active"));
      setLeaveRequests(leaveRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/leave-requests`, formData);
      toast.success("Leave request submitted successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit leave request");
    }
  };

  const handleApprove = async (status) => {
    try {
      await axios.put(`${API_URL}/leave-requests/${selectedRequest.id}`, { status });
      toast.success(`Leave request ${status}`);
      setApproveDialogOpen(false);
      setSelectedRequest(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update leave request");
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      employee_name: "",
      leave_type: "annual",
      start_date: "",
      end_date: "",
      days: 1,
      reason: ""
    });
  };

  const handleEmployeeChange = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    setFormData({
      ...formData,
      employee_id: employeeId,
      employee_name: employee?.name || ""
    });
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 1;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleDateChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    if (newData.start_date && newData.end_date) {
      newData.days = calculateDays(newData.start_date, newData.end_date);
    }
    setFormData(newData);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      approved: "border-green-500/50 text-green-400 bg-green-500/10",
      rejected: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.pending;
  };

  const getLeaveTypeBadge = (type) => {
    const styles = {
      annual: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      sick: "border-red-500/50 text-red-400 bg-red-500/10",
      casual: "border-purple-500/50 text-purple-400 bg-purple-500/10"
    };
    return styles[type] || styles.annual;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading leave data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-balance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <CalendarRange className="w-8 h-8 text-chart-4" />
            Leave Balance & Requests
          </h1>
          <p className="text-muted-foreground mt-1">Manage employee leave balances and requests</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="apply-leave-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Apply Leave
        </Button>
      </div>

      {/* Employee Leave Balances */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">Employee Leave Balances</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Employee</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Department</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-center">Annual Leave</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-center">Sick Leave</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-center">Casual Leave</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-center">Total Available</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No employees found.
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((employee) => (
                  <TableRow key={employee.id} className="border-border table-row-hover" data-testid={`employee-leave-row-${employee.id}`}>
                    <TableCell className="font-medium text-foreground">{employee.name}</TableCell>
                    <TableCell className="text-muted-foreground">{employee.department}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="border-blue-500/50 text-blue-400 bg-blue-500/10">
                        {employee.annual_leave_balance || 24}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="border-red-500/50 text-red-400 bg-red-500/10">
                        {employee.sick_leave_balance || 12}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="border-purple-500/50 text-purple-400 bg-purple-500/10">
                        {employee.casual_leave_balance || 6}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-mono font-bold text-primary">
                      {(employee.annual_leave_balance || 24) + (employee.sick_leave_balance || 12) + (employee.casual_leave_balance || 6)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Leave Requests */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">Leave Requests</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Employee</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Leave Type</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Dates</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Days</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Reason</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaveRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No leave requests found.
                  </TableCell>
                </TableRow>
              ) : (
                leaveRequests.map((request) => (
                  <TableRow key={request.id} className="border-border table-row-hover" data-testid={`leave-request-row-${request.id}`}>
                    <TableCell className="font-medium text-foreground">{request.employee_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getLeaveTypeBadge(request.leave_type)}>
                        {request.leave_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-sm">
                      {request.start_date} to {request.end_date}
                    </TableCell>
                    <TableCell className="font-mono text-foreground">{request.days}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">{request.reason}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getStatusBadge(request.status)}>
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {request.status === "pending" && (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-green-400 hover:text-green-300"
                            onClick={() => { setSelectedRequest(request); setApproveDialogOpen(true); }}
                            data-testid={`approve-leave-${request.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => handleApprove("rejected")}
                            data-testid={`reject-leave-${request.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Apply Leave Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Apply for Leave</DialogTitle>
            <DialogDescription>Submit a leave request for approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="employee">Employee</Label>
              <Select value={formData.employee_id} onValueChange={handleEmployeeChange}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="leave-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>{employee.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="leave_type">Leave Type</Label>
              <Select value={formData.leave_type} onValueChange={(v) => setFormData({ ...formData, leave_type: v })}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="leave-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Annual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="casual">Casual Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleDateChange("start_date", e.target.value)}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="leave-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleDateChange("end_date", e.target.value)}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="leave-end-date-input"
                />
              </div>
            </div>

            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Total Days: <span className="font-mono font-bold text-primary">{formData.days}</span></p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="bg-secondary/50 border-border min-h-[80px]"
                placeholder="Please provide a reason for leave..."
                required
                data-testid="leave-reason-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="leave-submit-btn">
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Approve Leave Request</DialogTitle>
            <DialogDescription>
              Approve leave request for {selectedRequest?.employee_name}?
              <br />
              <span className="font-mono text-sm">{selectedRequest?.start_date} to {selectedRequest?.end_date} ({selectedRequest?.days} days)</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleApprove("rejected")}>Reject</Button>
            <Button className="glow-primary bg-primary" onClick={() => handleApprove("approved")} data-testid="confirm-approve-leave">
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveBalancePage;
